// ANY EDITS TO THIS PAGE SHOULD ALSO BE MADE TO ITS DEMO COUNTERPART, TO KEEP THE APP AND DEMO IN SYNC
import './TryoutAthletesPage.css';
import './AddAthletePage.css';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getFirestore } from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import type {
  AthleteAddedToastPayload,
} from '../components/common/AthleteAddedToast/AthleteAddedToast';
import AthleteAddedToast
  from '../components/common/AthleteAddedToast/AthleteAddedToast';
import AthleteAddFailureToast
  from '../components/common/AthleteAddFailureToast/AthleteAddFailureToast';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PageTopNav from '../components/common/PageTopNav/PageTopNav';
import PhotoUpload from '../components/common/PhotoUpload/PhotoUpload';
import { useScoringAuth } from '../contexts/ScoringAuthContext';
import { usePostHog } from '@posthog/react';
import { scoringApp } from '../firebase';
import {
  addAthlete,
  fetchTryoutAddAthleteContext,
  TryoutAddAthleteContext,
} from '../services/athleteService';
import type { AthleteStatus } from '../types/athlete';
import { isValidEmail } from '../utils/email';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; tryout: TryoutAddAthleteContext };

type SubmitState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string };

const ADD_ATHLETE_SUCCESS_TOAST_MS = 5000;
const ADD_ATHLETE_FAILURE_TOAST_MS = 10000;

type OutcomeToast =
  | { kind: 'success'; payload: AthleteAddedToastPayload }
  | { kind: 'failure'; message: string };

const AddAthletePage: React.FC = () => {
  const { tryoutId } = useParams<{ tryoutId: string }>();
  const navigate = useNavigate();
  const { scoringUser } = useScoringAuth();
  const posthog = usePostHog();
  const db = getFirestore(scoringApp);

  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [returningstatus, setReturningStatus] = useState<AthleteStatus>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageThumbnailUrl, setImageThumbnailUrl] = useState('');
  const [imageStoragePathFull, setImageStoragePathFull] = useState('');
  const [imageStoragePathThumb, setImageStoragePathThumb] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadResetKey, setPhotoUploadResetKey] = useState(0);
  const [submitState, setSubmitState] = useState<SubmitState>({ status: 'idle' });
  const [outcomeToast, setOutcomeToast] = useState<OutcomeToast | null>(null);
  const [outcomeToastOpen, setOutcomeToastOpen] = useState(true);
  const outcomeToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const clearOutcomeToastTimer = useCallback((): void => {
    if (outcomeToastTimerRef.current != null) {
      clearTimeout(outcomeToastTimerRef.current);
      outcomeToastTimerRef.current = null;
    }
  }, []);

  const dismissOutcomeToast = useCallback((): void => {
    clearOutcomeToastTimer();
    setOutcomeToastOpen(false);
  }, [clearOutcomeToastTimer]);

  const handleOutcomeToastExitComplete = useCallback((): void => {
    setOutcomeToast(null);
  }, []);

  const resetAddAthleteFormFields = useCallback((): void => {
    setName('');
    setNumber('');
    setEmail('');
    setImageUrl('');
    setImageThumbnailUrl('');
    setImageStoragePathFull('');
    setImageStoragePathThumb('');
    setPhotoUploading(false);
    if (loadState.status === 'ready') {
      const { tryout } = loadState;
      setPosition(tryout.athletePositions[0] ?? 'Unknown');
      setReturningStatus(tryout.athleteStatuses[0] ?? null);
    }
    setPhotoUploadResetKey((k) => k + 1);
  }, [loadState]);

  const showOutcomeToast = useCallback(
    (next: OutcomeToast): void => {
      clearOutcomeToastTimer();
      setOutcomeToast(next);
      setOutcomeToastOpen(true);
      const ms =
        next.kind === 'failure'
          ? ADD_ATHLETE_FAILURE_TOAST_MS
          : ADD_ATHLETE_SUCCESS_TOAST_MS;
      outcomeToastTimerRef.current = setTimeout(() => {
        outcomeToastTimerRef.current = null;
        setOutcomeToastOpen(false);
      }, ms);
    },
    [clearOutcomeToastTimer],
  );

  useEffect(() => () => clearOutcomeToastTimer(), [clearOutcomeToastTimer]);

  useEffect(() => {
    if (!tryoutId) {
      setLoadState({ status: 'error', message: 'Invalid tryout link.' });
      return;
    }

    let cancelled = false;
    setLoadState({ status: 'loading' });

    fetchTryoutAddAthleteContext(db, tryoutId)
      .then((tryout) => {
        if (!cancelled) {
          setLoadState({ status: 'ready', tryout });
          const pos = tryout.athletePositions[0] ?? 'Unknown';
          setPosition(pos);
          setReturningStatus(tryout.athleteStatuses[0] ?? null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load tryout.';
          setLoadState({ status: 'error', message });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [db, tryoutId]);

  const isSubmitting = submitState.status === 'submitting';
  /** Only the Add Athlete button waits for photo upload; other controls stay usable. */
  const addAthleteSubmitLocked = isSubmitting || photoUploading;
  const formFieldsLocked = isSubmitting;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedNumber = number.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setSubmitState({ status: 'error', message: 'Athlete name is required.' });
      return;
    }
    if (!trimmedNumber) {
      setSubmitState({ status: 'error', message: 'Athlete number is required.' });
      return;
    }
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      setSubmitState({ status: 'error', message: 'Enter a valid athlete email.' });
      return;
    }
    if (!tryoutId) {
      setSubmitState({ status: 'error', message: 'Invalid tryout link.' });
      return;
    }
    if (!scoringUser) {
      setSubmitState({ status: 'error', message: 'You must be logged in.' });
      return;
    }
    if (loadState.status !== 'ready') {
      setSubmitState({ status: 'error', message: 'Tryout is still loading.' });
      return;
    }

    const { tryout } = loadState;
    const positionChoices =
      tryout.athletePositions.length > 0 ? tryout.athletePositions : ['Unknown'];
    if (!positionChoices.includes(position)) {
      setSubmitState({ status: 'error', message: 'Pick a valid position.' });
      return;
    }
    const statusChoices = tryout.athleteStatuses;
    if (
      statusChoices.length > 0
      && (returningstatus === null || !statusChoices.includes(returningstatus))
    ) {
      setSubmitState({ status: 'error', message: 'Pick a valid status.' });
      return;
    }

    setSubmitState({ status: 'submitting' });

    const successPayload: AthleteAddedToastPayload = {
      name: trimmedName,
      number: trimmedNumber,
      position,
      returningstatus: statusChoices.length === 0 ? null : returningstatus,
      imageThumbnailUrl: imageThumbnailUrl.trim(),
    };

    try {
      await addAthlete(db, tryoutId, {
        name: trimmedName,
        number: trimmedNumber,
        email: trimmedEmail,
        position,
        returningstatus: statusChoices.length === 0 ? null : returningstatus,
        createdBy: scoringUser.uid,
        imageUrl: imageUrl.trim(),
        imageThumbnailUrl: imageThumbnailUrl.trim(),
      });
      posthog?.capture('athlete_added', { tryout_id: tryoutId, position });
      contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      resetAddAthleteFormFields();
      setSubmitState({ status: 'idle' });
      showOutcomeToast({ kind: 'success', payload: successPayload });
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to add athlete. Please try again.';
      setSubmitState({ status: 'idle' });
      showOutcomeToast({ kind: 'failure', message });
    }
  };

  const header = (
    <PageTopNav
      onBack={() => navigate(`/tryout/${tryoutId}/athletes`)}
      backAriaLabel="Back to athlete list"
      title={loadState.status === 'ready' ? loadState.tryout.name : ''}
    />
  );

  if (loadState.status === 'loading') {
    return (
      <div className="tryout-athletes-shell add-athlete-shell">
        {header}
        <div className="add-athletes-content tryout-athletes-content--centered">
          <p className="add-athlete-loading-text">Loading…</p>
        </div>
      </div>
    );
  }

  if (loadState.status === 'error') {
    return (
      <div className="tryout-athletes-shell add-athlete-shell">
        {header}
        <div className="add-athletes-content tryout-athletes-content--centered">
          <p className="tryout-athletes-error" role="alert">
            {loadState.message}
          </p>
          <Button
            variant="secondary"
            onClick={() => navigate(`/tryout/${tryoutId}/athletes`)}
          >
            Back to list
          </Button>
        </div>
      </div>
    );
  }

  const { tryout } = loadState;
  const positionChoices =
    tryout.athletePositions.length > 0 ? tryout.athletePositions : ['Unknown'];
  const statusChoices = tryout.athleteStatuses;

  return (
    <>
      <div className="tryout-athletes-shell add-athlete-shell">
        {header}

        <div ref={contentScrollRef} className="tryout-athletes-content">
          <div className="add-athlete-content-panel">
            <h2 className="add-athlete-heading">Add Athlete</h2>

            {submitState.status === 'error' && (
              <div className="add-athlete-error" role="alert">
                {submitState.message}
              </div>
            )}

            <form
              className="add-athlete-form"
              onSubmit={(e) => {
                void handleSubmit(e);
              }}
            >
          <Input
            type="text"
            label="Athlete Name"
            placeholder=""
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={formFieldsLocked}
            autoCapitalize="words"
          />

          <Input
            type="tel"
            label="Athlete Number"
            placeholder=""
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            required
            disabled={formFieldsLocked}
            inputMode="numeric"
          />

          <Input
            type="email"
            label="Athlete Email"
            placeholder=""
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={formFieldsLocked}
            inputMode="email"
            autoCapitalize="off"
            autoComplete="email"
          />

          {scoringUser ? (
            <PhotoUpload
              key={photoUploadResetKey}
              tryoutId={tryoutId ?? ''}
              coachUid={scoringUser.uid}
              disabled={formFieldsLocked}
              label="Athlete photo"
              className="add-athlete-photo-upload"
              imageUrl={imageUrl}
              storagePathFull={imageStoragePathFull}
              storagePathThumb={imageStoragePathThumb}
              onImageUpload={(payload) => {
                setImageUrl(payload.imageUrl);
                setImageThumbnailUrl(payload.imageThumbnailUrl);
                setImageStoragePathFull(payload.storagePathFull);
                setImageStoragePathThumb(payload.storagePathThumb);
              }}
              onUploadingChange={setPhotoUploading}
            />
          ) : null}

          <div className="add-athlete-field-group">
            <div className="add-athlete-field-label" id="add-athlete-position-label">
              Position
            </div>
            <div
              className="tryout-athletes-filter-row add-athlete-chip-row"
              role="group"
              aria-labelledby="add-athlete-position-label"
            >
              {positionChoices.map((pos) => (
                <button
                  key={pos}
                  type="button"
                  className={[
                    'tryout-athletes-filter-chip',
                    position === pos ? 'tryout-athletes-filter-chip--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setPosition(pos)}
                  disabled={formFieldsLocked}
                  aria-pressed={position === pos}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>

          {statusChoices.length > 0 ? (
            <div className="add-athlete-field-group">
              <div className="add-athlete-field-label" id="add-athlete-status-label">
                Status
              </div>
              <div
                className="tryout-athletes-filter-row add-athlete-chip-row"
                role="group"
                aria-labelledby="add-athlete-status-label"
              >
                {statusChoices.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className={[
                      'tryout-athletes-filter-chip',
                      returningstatus === label ? 'tryout-athletes-filter-chip--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setReturningStatus(label)}
                    disabled={formFieldsLocked}
                    aria-pressed={returningstatus === label}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="add-athlete-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/tryout/${tryoutId}/athletes`)}
              disabled={formFieldsLocked}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={addAthleteSubmitLocked}
              aria-busy={photoUploading || isSubmitting}
            >
              {isSubmitting ? (
                'Adding…'
              ) : photoUploading ? (
                <span className="add-athlete-submit-uploading">
                  <span>Uploading Photo</span>
                  <span
                    className="add-athlete-submit-spinner"
                    aria-hidden="true"
                  />
                </span>
              ) : (
                'Add Athlete'
              )}
            </Button>
          </div>
            </form>
          </div>
        </div>

        {false ? (
          <div className="add-athlete-debug">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                showOutcomeToast({
                  kind: 'failure',
                  message:
                    'Debug: simulated save failure (no server request was made).',
                });
              }}
            >
              Preview failure toast
            </Button>
          </div>
        ) : null}
      </div>
      
      {outcomeToast?.kind === 'success' ? (
        <AthleteAddedToast
          open={outcomeToastOpen}
          payload={outcomeToast.payload}
          onDismiss={dismissOutcomeToast}
          onExitComplete={handleOutcomeToastExitComplete}
        />
      ) : null}
      {outcomeToast?.kind === 'failure' ? (
        <AthleteAddFailureToast
          open={outcomeToastOpen}
          message={outcomeToast.message}
          onDismiss={dismissOutcomeToast}
          onExitComplete={handleOutcomeToastExitComplete}
        />
      ) : null}
    </>
  );
};

export default AddAthletePage;
